// KamSathi Admin — Contractor App Service
// Full read/write access to kamsathi-27dba
// Replicates all functions from contractor app's firebase.js

import { contractorDb } from './firebase';
import {
  collection, getDocs, doc, updateDoc, addDoc, deleteDoc,
  query, where, orderBy, getDoc, serverTimestamp
} from 'firebase/firestore';

// ── Google Sheets URLs ──
const CUSTOMER_SHEET_URL = 'https://script.google.com/macros/s/AKfycbxg5h35bJWZVc0NdZRBQVPFWiKQ-45rBeVVJmlEsu9KQ1p81xUeexcwe-K2e4bbmRUZ/exec';
const CONTRACTOR_SHEET_URL = 'https://script.google.com/macros/s/AKfycby-2VnIRUbq4RyN3Xl98PmW7Mc4dxp0nbZ7AlFCkXGDnC13W8Sx5PDFOY7fzZjChk3t/exec';

// ════════════════════════════════════════
// LEADS
// ════════════════════════════════════════

export const getLeads = async (statusFilter = null) => {
  try {
    const q = statusFilter
      ? query(collection(contractorDb, 'leads'), where('status', '==', statusFilter))
      : query(collection(contractorDb, 'leads'), orderBy('timestamp', 'desc'));
    const snap = await getDocs(q);
    const seen = new Set();
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(x => {
        const k = x.phone?.toString().trim();
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
  } catch (error) {
    console.error('getLeads error:', error);
    return [];
  }
};

export const getLeadStats = async () => {
  try {
    const snap = await getDocs(collection(contractorDb, 'leads'));
    const leads = snap.docs.map(d => d.data());
    const seen = new Set();
    const unique = leads.filter(x => {
      const k = x.phone?.toString().trim();
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    return {
      total: unique.length,
      pending: unique.filter(l => l.status === 'pending').length,
      verified: unique.filter(l => l.status === 'verified').length,
      rejected: unique.filter(l => l.status === 'rejected').length,
      website: unique.filter(l => l.source === 'website').length,
      totalUnlocks: unique.reduce((s, l) => s + (l.unlockCount || 0), 0),
      totalRevenue: unique.reduce((s, l) => s + ((l.unlockCount || 0) * getLeadPrice(l.budget)), 0),
    };
  } catch (error) {
    return { total: 0, pending: 0, verified: 0, rejected: 0, website: 0, totalUnlocks: 0, totalRevenue: 0 };
  }
};

export const saveLead = async (leadData) => {
  const r = await addDoc(collection(contractorDb, 'leads'), {
    ...leadData,
    status: 'pending',
    unlockedBy: [],
    unlockCount: 0,
    maxUnlocks: 7,
    timestamp: new Date().toISOString(),
  });
  return r.id;
};

export const verifyLead = async (leadId) => {
  await updateDoc(doc(contractorDb, 'leads', leadId), { status: 'verified' });
};

export const rejectLead = async (leadId) => {
  await updateDoc(doc(contractorDb, 'leads', leadId), { status: 'rejected' });
};

export const updateLead = async (leadId, data) => {
  await updateDoc(doc(contractorDb, 'leads', leadId), data);
};

export const deleteLead = async (leadId) => {
  await deleteDoc(doc(contractorDb, 'leads', leadId));
};

// ════════════════════════════════════════
// CONTRACTORS
// ════════════════════════════════════════

export const getContractors = async () => {
  try {
    const snap = await getDocs(collection(contractorDb, 'contractors'));
    const seen = new Map();
    snap.docs.forEach(d => {
      const data = d.data();
      const k = data.phone?.toString().trim();
      if (!k) return;
      if (!seen.has(k) || data.status === 'verified') seen.set(k, { id: d.id, ...data });
    });
    return Array.from(seen.values()).sort((a, b) =>
      new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );
  } catch (error) {
    console.error('getContractors error:', error);
    return [];
  }
};

export const getContractorStats = async () => {
  try {
    const snap = await getDocs(collection(contractorDb, 'contractors'));
    const seen = new Map();
    snap.docs.forEach(d => {
      const data = d.data();
      const k = data.phone?.toString().trim();
      if (!k) return;
      if (!seen.has(k) || data.status === 'verified') seen.set(k, data);
    });
    const contractors = Array.from(seen.values());
    return {
      total: contractors.length,
      verified: contractors.filter(c => c.status === 'verified').length,
      pending: contractors.filter(c => c.status === 'pending').length,
      rejected: contractors.filter(c => c.status === 'rejected').length,
    };
  } catch (error) {
    return { total: 0, verified: 0, pending: 0, rejected: 0 };
  }
};

export const verifyContractor = async (contractorId, loginId, password) => {
  await updateDoc(doc(contractorDb, 'contractors', contractorId), {
    status: 'verified',
    loginId,
    password,
  });
};

export const updateContractorStatus = async (contractorId, newStatus, adminEmail) => {
  try {
    await updateDoc(doc(contractorDb, 'contractors', contractorId), {
      status: newStatus,
      reviewedAt: new Date().toISOString(),
      reviewedBy: adminEmail,
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const saveContractor = async (contractorData) => {
  const r = await addDoc(collection(contractorDb, 'contractors'), {
    ...contractorData,
    status: 'pending',
    timestamp: new Date().toISOString(),
  });
  return r.id;
};

export const updateContractor = async (contractorId, data) => {
  await updateDoc(doc(contractorDb, 'contractors', contractorId), data);
};

export const deleteContractor = async (contractorId) => {
  await deleteDoc(doc(contractorDb, 'contractors', contractorId));
};

export const rateContractor = async (contractorId, rating) => {
  await updateDoc(doc(contractorDb, 'contractors', contractorId), { rating });
};

// ════════════════════════════════════════
// PAYMENTS
// ════════════════════════════════════════

export const getPayments = async () => {
  try {
    const snap = await getDocs(collection(contractorDb, 'payments'));
    const seen = new Map();
    snap.docs.forEach(d => {
      const data = d.data();
      const k = (data.leadId || data.id) + '_' + (data.contractorId || data.contractorName || '');
      if (!seen.has(k) || data.status === 'confirmed') seen.set(k, { id: d.id, ...data });
    });
    return Array.from(seen.values()).sort((a, b) =>
      new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
    );
  } catch (error) {
    console.error('getPayments error:', error);
    return [];
  }
};

export const getPaymentStats = async () => {
  try {
    const payments = await getPayments();
    return {
      total: payments.length,
      confirmed: payments.filter(p => p.status === 'confirmed').length,
      pending: payments.filter(p => p.status === 'pending_verification').length,
      totalRevenue: payments.filter(p => p.status === 'confirmed').reduce((s, p) => s + (p.amount || 0), 0),
    };
  } catch (error) {
    return { total: 0, confirmed: 0, pending: 0, totalRevenue: 0 };
  }
};

export const confirmPayment = async (paymentId, leadId, contractorId) => {
  await updateDoc(doc(contractorDb, 'payments', paymentId), { status: 'confirmed' });
  // Unlock lead for this contractor
  const leadRef = doc(contractorDb, 'leads', leadId);
  const leadSnap = await getDoc(leadRef);
  if (leadSnap.exists()) {
    const data = leadSnap.data();
    const unlockedBy = data.unlockedBy || [];
    if (!unlockedBy.includes(contractorId)) {
      await updateDoc(leadRef, {
        unlockedBy: [...unlockedBy, contractorId],
        unlockCount: unlockedBy.length + 1,
      });
    }
  }
};

export const rejectPayment = async (paymentId) => {
  await updateDoc(doc(contractorDb, 'payments', paymentId), {
    status: 'rejected',
    rejectedAt: new Date().toISOString(),
  });
};

// ════════════════════════════════════════
// JOB COMPLETIONS
// ════════════════════════════════════════

export const getJobCompletions = async () => {
  try {
    const snap = await getDocs(collection(contractorDb, 'jobCompletions'));
    const seen = new Set();
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(comp => {
        const key = (comp.leadId || '') + '_' + (comp.contractorId || comp.contractorName || '') + '_' + (comp.stageType || comp.stage || 'unknown');
        if (seen.has(key)) return false;
        seen.add(key);
        return comp.status !== 'cancelled' && (comp.stageType || comp.stage);
      });
  } catch (error) {
    console.error('getJobCompletions error:', error);
    return [];
  }
};

export const getCompletionStats = async () => {
  try {
    const completions = await getJobCompletions();
    return {
      total: completions.length,
      pending: completions.filter(c => c.status === 'pending').length,
      verified: completions.filter(c => c.status === 'verified').length,
      rejected: completions.filter(c => c.status === 'rejected').length,
    };
  } catch (error) {
    return { total: 0, pending: 0, verified: 0, rejected: 0 };
  }
};

export const verifyJobCompletion = async (id) => {
  await updateDoc(doc(contractorDb, 'jobCompletions', id), {
    status: 'verified',
    verifiedAt: new Date().toISOString(),
  });
};

export const rejectJobCompletion = async (id, note) => {
  await updateDoc(doc(contractorDb, 'jobCompletions', id), {
    status: 'rejected',
    rejectedAt: new Date().toISOString(),
    adminNote: note || '',
  });
};

// ════════════════════════════════════════
// GOOGLE SHEETS SYNC
// ════════════════════════════════════════

export const fetchLeadsFromSheets = async () => {
  try {
    const res = await fetch(`${CUSTOMER_SHEET_URL}?action=getLeads`);
    const text = await res.text();
    const json = JSON.parse(text);
    if (!json.leads) return [];
    return json.leads.map((row, i) => ({
      id: `sheet_${i + 2}`,
      rowIndex: i + 2,
      name: row.name || '',
      phone: row.phone || '',
      area: row.area || '',
      property: row.property || '',
      workType: row.workType || '',
      budget: row.budget || '',
      timeline: row.timeline || '',
      size: row.size || '',
      callTime: row.callTime || '',
      source: 'website',
      notes: row.notes || '',
      status: 'pending',
      createdAt: row.timestamp || new Date().toISOString(),
    }));
  } catch (e) {
    console.error('fetchLeadsFromSheets error:', e);
    return [];
  }
};

export const fetchContractorsFromSheets = async () => {
  try {
    const res = await fetch(`${CONTRACTOR_SHEET_URL}?action=getContractors`);
    const text = await res.text();
    const json = JSON.parse(text);
    if (!json.contractors) return [];
    return json.contractors.map((row, i) => ({
      id: `sheet_c_${i + 2}`,
      rowIndex: i + 2,
      name: row.fullName || row.name || '',
      phone: row.whatsapp || row.phone || '',
      experience: row.experience || '',
      teamSize: row.teamSize || '',
      workTypes: row.workTypes ? row.workTypes.split(', ') : [],
      areas: row.serveAreas ? row.serveAreas.split(', ') : [],
      area: row.baseLocation || row.baseArea || row.area || '',
      baseLocation: row.baseLocation || '',
      source: 'website',
      status: 'pending',
      createdAt: row.timestamp || new Date().toISOString(),
    }));
  } catch (e) {
    console.error('fetchContractorsFromSheets error:', e);
    return [];
  }
};

export const syncFromSheets = async () => {
  const [sheetLeads, sheetContractors, existingLeads, existingContractors] = await Promise.all([
    fetchLeadsFromSheets(),
    fetchContractorsFromSheets(),
    getLeads(),
    getContractors(),
  ]);

  const existingPhones = new Set(existingLeads.map(l => l.phone?.toString().trim()));
  const existingContractorPhones = new Set(existingContractors.map(c => (c.phone || c.whatsapp)?.toString().trim()));

  let newLeadsCount = 0;
  let newContractorsCount = 0;

  for (const lead of sheetLeads) {
    if (!existingPhones.has(lead.phone?.toString().trim())) {
      await saveLead({ ...lead, source: 'website', status: 'pending' });
      newLeadsCount++;
    }
  }

  for (const contractor of sheetContractors) {
    const phone = (contractor.phone || contractor.whatsapp)?.toString().trim();
    if (!existingContractorPhones.has(phone)) {
      await saveContractor({ ...contractor, source: 'website', status: 'pending' });
      newContractorsCount++;
    }
  }

  return {
    sheetLeadsTotal: sheetLeads.length,
    sheetContractorsTotal: sheetContractors.length,
    newLeadsAdded: newLeadsCount,
    newContractorsAdded: newContractorsCount,
  };
};

// ════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════

export const getLeadPrice = (budget) => {
  if (!budget) return 11;
  const b = budget.toLowerCase();
  if (b.includes('under') || (b.includes('20k') && !b.includes('50k'))) return 11;
  if (b.includes('50k') && !b.includes('1l') && !b.includes('1 lakh')) return 11;
  if ((b.includes('50k') || b.includes('50,000')) && (b.includes('1l') || b.includes('1 lakh'))) return 51;
  if ((b.includes('1l') || b.includes('1 lakh')) && (b.includes('3l') || b.includes('3 lakh'))) return 101;
  if (b.includes('3l') || b.includes('3 lakh') || b.includes('more')) return 201;
  return 11;
};

export const getAdminConfig = async () => {
  try {
    const snap = await getDoc(doc(contractorDb, 'config', 'admin'));
    if (snap.exists()) return snap.data();
    return {};
  } catch (error) {
    return {};
  }
};